import { createContext, useMemo } from 'react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type GlobalToken, notification, theme } from 'antd';

import './NotificationsContext.css';

interface NotificationsContextType {
  showSuccess(message: string, description?: string): void;
  showError(message: string, description?: string): void;
  showWarning(message: string, description?: string): void;
}

export const NotificationsContext = createContext<NotificationsContextType>({
  showSuccess() {
    // eslint-disable-next-line no-console
    console.log('NotificationsContext not found');
  },
  showError() {
    // eslint-disable-next-line no-console
    console.log('NotificationsContext not found');
  },
  showWarning() {
    // eslint-disable-next-line no-console
    console.log('NotificationsContext not found');
  },
});

const SUCCESS_DEFAULT_CONFIG = (token: GlobalToken) => ({
  duration: 3,
  className: 'avayler-notification',
  style: {
    background: token.colorSuccess,
  },
});

const ERROR_DEFAULT_CONFIG = (token: GlobalToken) => ({
  duration: 5,
  className: 'avayler-notification',
  style: {
    background: token.colorError,
  },
  icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
});

const WARNING_DEFAULT_CONFIG = (token: GlobalToken) => ({
  duration: 5,
  className: 'avayler-notification',
  style: {
    background: token.colorWarning,
  },
  icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
});

interface Props {
  children: React.ReactNode;
}

export function NotificationsContextProvider({ children }: Props) {
  const { token } = theme.useToken();
  const [api, contextHolder] = notification.useNotification();

  const context = useMemo(() => {
    const showSuccess = (message: string, description: string) => {
      api.success({ ...SUCCESS_DEFAULT_CONFIG(token), message, description });
    };

    const showError = (message: string, description: string) => {
      api.error({ ...ERROR_DEFAULT_CONFIG(token), message, description });
    };

    const showWarning = (message: string, description: string) => {
      api.warning({ ...WARNING_DEFAULT_CONFIG(token), message, description });
    };

    return { showSuccess, showError, showWarning };
  }, [api]);

  return (
    <NotificationsContext.Provider value={context}>
      {contextHolder}
      {children}
    </NotificationsContext.Provider>
  );
}
